import React, { useState, useRef } from 'react';
import {
  VStack,
  Card,
  CardHeader,
  CardBody,
  FormControl,
  FormLabel,
  Select,
  Heading,
  useColorModeValue,
  SimpleGrid,
  Button,
  Input,
  HStack,
  Avatar,
  Text,
  Divider,
  useToast,
  IconButton,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FiEdit2, FiLogOut, FiCamera, FiPlus, FiCopy, FiTrash2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';

const MotionCard = motion(Card);

const Settings = () => {
  const fileInputRef = useRef(null);
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [settings, setSettings] = useState({
    appearance: {
      theme: 'light',
    },
    profile: {
      name: 'John Doe',
      email: 'john@example.com',
      avatar: 'https://bit.ly/dan-abramov',
    }
  });

  const [apiKeys, setApiKeys] = useState([
    { id: 1, name: 'Production API', key: 'pk_live_123456789', user: 'John Doe', created: '2024-03-15', status: 'active' },
    { id: 2, name: 'Development API', key: 'pk_test_987654321', user: 'John Doe', created: '2024-03-14', status: 'active' },
  ]);

  const [newApiKey, setNewApiKey] = useState({
    name: '',
    user: '',
  });

  const [users] = useState([
    { id: 1, name: 'John Doe', email: 'john@example.com' },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com' },
    { id: 3, name: 'Bob Johnson', email: 'bob@example.com' },
  ]);

  const cardBg = useColorModeValue('white', 'gray.700');

  const handleChange = (category, setting, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: value,
      },
    }));
  };

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleChange('profile', 'avatar', reader.result);
        toast({
          title: 'Profile picture updated',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateProfile = () => {
    setTimeout(() => {
      toast({
        title: 'Profile updated',
        description: 'Your profile has been successfully updated.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }, 1000);
  };

  const handleLogout = () => {
    toast({
      title: 'Logging out...',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });

    setTimeout(() => {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      
      toast({
        title: 'Logged out successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

      navigate('/login');
    }, 2000);
  };

  const handleCreateApiKey = () => {
    const newKey = {
      id: apiKeys.length + 1,
      name: newApiKey.name,
      key: `pk_${Math.random().toString(36).substring(2, 15)}`,
      user: newApiKey.user,
      created: new Date().toISOString().split('T')[0],
      status: 'active'
    };

    setApiKeys([...apiKeys, newKey]);
    setNewApiKey({ name: '', user: '' });
    onClose();

    toast({
      title: 'API Key Created',
      description: 'New API key has been generated successfully.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleCopyApiKey = (key) => {
    navigator.clipboard.writeText(key);
    toast({
      title: 'API Key Copied',
      description: 'API key has been copied to clipboard.',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleDeleteApiKey = (id) => {
    setApiKeys(apiKeys.filter(key => key.id !== id));
    toast({
      title: 'API Key Deleted',
      description: 'API key has been deleted successfully.',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  return (
    <PageLayout title="Settings">
      <SimpleGrid 
        columns={{ base: 1, md: 2 }} 
        spacing={6}
        w="100%"
        maxW="800px"
        mx="auto"
      >
        <MotionCard whileHover={{ scale: 1.02 }} bg={cardBg}>
          <CardHeader>
            <Heading size="md">Profile</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack spacing={4} position="relative">
                <Box position="relative">
                  <Avatar 
                    size="lg" 
                    src={settings.profile.avatar}
                    cursor="pointer"
                    onClick={handleImageClick}
                  />
                  <IconButton
                    icon={<FiCamera />}
                    size="sm"
                    position="absolute"
                    bottom="0"
                    right="0"
                    colorScheme="blue"
                    rounded="full"
                    onClick={handleImageClick}
                  />
                </Box>
                <VStack align="start" spacing={1}>
                  <Text fontWeight="bold">{settings.profile.name}</Text>
                  <Text fontSize="sm" color="gray.500">{settings.profile.email}</Text>
                </VStack>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
              </HStack>
              <Divider />
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input 
                  value={settings.profile.name}
                  onChange={(e) => handleChange('profile', 'name', e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input 
                  value={settings.profile.email}
                  onChange={(e) => handleChange('profile', 'email', e.target.value)}
                />
              </FormControl>
              <Button 
                leftIcon={<FiEdit2 />} 
                colorScheme="blue"
                onClick={handleUpdateProfile}
              >
                Update Profile
              </Button>
            </VStack>
          </CardBody>
        </MotionCard>

        <MotionCard whileHover={{ scale: 1.02 }} bg={cardBg}>
          <CardHeader>
            <Heading size="md">Appearance</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Theme</FormLabel>
                <Select
                  value={settings.appearance.theme}
                  onChange={(e) => {
                    handleChange('appearance', 'theme', e.target.value);
                    toast({
                      title: 'Theme updated',
                      description: `Theme changed to ${e.target.value}`,
                      status: 'success',
                      duration: 2000,
                      isClosable: true,
                    });
                  }}
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="system">System</option>
                </Select>
              </FormControl>
            </VStack>
          </CardBody>
        </MotionCard>
        <MotionCard whileHover={{ scale: 1.02 }} bg={cardBg}>
          <CardHeader>
            <Heading size="md">Account</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Button 
                leftIcon={<FiLogOut />} 
                colorScheme="red" 
                variant="outline"
                onClick={handleLogout}
              >
                Logout
              </Button>
            </VStack>
          </CardBody>
        </MotionCard>
      </SimpleGrid>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New API Key</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>API Key Name</FormLabel>
                <Input
                  placeholder="e.g., Production API"
                  value={newApiKey.name}
                  onChange={(e) => setNewApiKey({ ...newApiKey, name: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Select User</FormLabel>
                <Select
                  placeholder="Select user"
                  value={newApiKey.user}
                  onChange={(e) => setNewApiKey({ ...newApiKey, user: e.target.value })}
                >
                  {users.map((user) => (
                    <option key={user.id} value={user.name}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </Select>
              </FormControl>
              <Button colorScheme="blue" onClick={handleCreateApiKey} width="100%">
                Generate API Key
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default Settings; 